import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { HelmetProvider } from "react-helmet-async"
import { store, persistor } from "./store/store"
import { Toaster } from "react-hot-toast"
import Layout from "./components/layout/Layout"
import AdminLayout from "./components/layout/AdminLayout"
import ProtectedRoute from "./components/ProtectedRoute"
import { Suspense, lazy } from "react"
import LoadingSpinner from "./components/common/LoadingSpinner"
import ErrorBoundary from "./components/common/ErrorBoundary"

// Lazy load all pages
const Home = lazy(() => import("./pages/Home"))
const Categories = lazy(() => import("./pages/Categories"))
const Cart = lazy(() => import("./pages/Cart"))
const Checkout = lazy(() => import("./pages/Checkout"))
const Orders = lazy(() => import("./pages/Orders"))
const Wishlist = lazy(() => import("./pages/Wishlist"))
const Search = lazy(() => import("./pages/Search"))
const Profile = lazy(() => import("./pages/Profile"))
const Login = lazy(() => import("./pages/Auth/Login"))
const Signup = lazy(() => import("./pages/Auth/Signup"))
const ProductDetail = lazy(() => import("./pages/ProductDetail"))
const Products = lazy(() => import("./pages/Products"))

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/Admin/Dashboard"))
const AdminProducts = lazy(() => import("./pages/Admin/Products"))
const AdminAddProduct = lazy(() => import("./pages/Admin/AddProduct"))
const AdminEditProduct = lazy(() => import("./pages/Admin/EditProduct"))
const AdminOrders = lazy(() => import("./pages/Admin/Orders"))
const AdminCategories = lazy(() => import("./pages/Admin/Categories"))
const AdminSettings = lazy(() => import("./pages/Admin/Settings"))
const AdminUserManagement = lazy(() => import("./pages/Admin/UserManagement"))
const AdminLogout = lazy(() => import("./pages/AdminLogout"))
const AdminCustomers = lazy(() => import("./pages/AdminCustomers"))

function AppContent() {
  return (
    <Router>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>}>
        <Routes>
          {/* Auth Routes (no layout) */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/register" element={<Signup />} />
          
          {/* Main App Routes with Layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="categories" element={<Categories />} />
            <Route path="categories/:category" element={<Categories />} />
            <Route path="search" element={<Search />} />
            <Route path=":category/:productName" element={<ProductDetail />} />
            <Route path="cart" element={<Cart />} />
            
            {/* Protected Routes */}
            <Route
              path="checkout"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="wishlist"
              element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
          </Route>
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAuth={true} requireAdmin={true}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/add" element={<AdminAddProduct />} />
            <Route path="products/edit/:id" element={<AdminEditProduct />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="users" element={<AdminUserManagement />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="logout" element={<AdminLogout />} />
            <Route path="customers" element={<AdminCustomers />} />
          </Route>

          
          {/* 404 Not Found */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#363636",
                color: "#fff",
              },
            }}
          />
        </PersistGate>
      </Provider>
    </HelmetProvider>
  )
}

export default App
